/**
 * x-select directive.
 *
 * A fully accessible custom select component backed by a native `<select>`
 * element. The native select is hidden visually but remains in the DOM for
 * form submission and programmatic value access. The custom UI mirrors the
 * native select's option list reactively via a MutationObserver.
 *
 * Sub-directives:
 *   x-select:input  - Applied to the native `<select>` element. Hides the
 *                     parent wrapper, observes option and attribute changes,
 *                     and propagates `change` events and x-model updates.
 *   x-select:label  - Applied to the label element that displays the current
 *                     selection text. Also sets the `title` attribute.
 *   x-select:toggle - Applied to the trigger button that opens/closes the menu.
 *   x-select:menu   - Applied to the listbox dropdown. Handles outside-click
 *                     dismissal, keyboard navigation (arrows, home, end, page
 *                     up/down, escape), focus trapping, and visibility.
 *
 * @options (root only)
 *   closeOnSelection - Automatically close the menu when an option is selected
 *                      (default: true).
 *
 * @example
 *   <div x-select>
 *     <select x-select:input name="size">
 *       <option value="s">Small</option>
 *       <option value="m" selected>Medium</option>
 *     </select>
 *     <button x-select:toggle>
 *       <span x-select:label></span>
 *     </button>
 *     <ul x-select:menu role="listbox">…</ul>
 *   </div>
 */
(() => {
  /**
   * Displays the label of the currently selected option (or the first option
   * as a fallback) and sets it as the `title` attribute.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The label element.
   */
  const label = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["x-ref"]: "selectLabel",

      ["x-data"]() {
        return {
          /**
           * Returns the label text of the selected option, or the first
           * option's label when nothing is explicitly selected.
           * @returns {string|undefined}
           */
          get text() {
            const selected = this.getSelected();

            return selected ? selected.label : this.options[0]?.label;
          },
        };
      },

      ["x-text"]() {
        return this.text;
      },

      [":title"]() {
        return this.text;
      },
    }));
  };

  /**
   * Hides the native `<select>`'s parent wrapper, observes option list and
   * `selected` attribute mutations, and keeps the parent component's `options`
   * array and x-model bindings in sync.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLSelectElement} el - The native select element.
   */
  const input = (Alpine, el) => {
    let observer;

    Alpine.bind(el, () => ({
      ["x-ref"]: "selectInput",
      ["x-data"]() {
        return {
          /**
           * Hides the native select's wrapper and starts a MutationObserver to
           * watch for option list changes and `selected` attribute mutations.
           */
          init() {
            this.$el.parentNode.setAttribute("hidden", "");

            observer = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if ("childList" === mutation.type) {
                  this.$dispatch("options-changed");
                }
              });

              const attributeSelectedChange = mutations.find(
                (mutation) => "attributes" === mutation.type,
              );

              if (attributeSelectedChange) {
                this.$dispatch("change");

                if (
                  this.$el._x_model &&
                  "function" === typeof this.$el._x_model.set
                ) {
                  this.$el._x_model.set(this.$el.value);
                }
              }
            });

            observer.observe(this.$el, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ["selected"],
            });
          },

          /**
           * Restores the native select's wrapper visibility and disconnects
           * the MutationObserver.
           */
          destroy() {
            this.$el.parentNode.removeAttribute("hidden");

            observer.disconnect();
            observer = null;
          },
        };
      },
    }));
  };

  /**
   * Binds the toggle button with click and keyboard handlers and sets
   * `aria-expanded` to reflect the open state.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The toggle button element.
   */
  const toggle = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["x-ref"]: "selectToggle",

      ["@click"]() {
        this.openSelectMenu();
      },

      ["@keydown.down.prevent.stop"]() {
        this.openSelectMenu();
      },

      ["@keydown.up.prevent.stop"]() {
        this.openSelectMenu();
      },

      [":aria-expanded"]: "opened",
    }));
  };

  /**
   * Binds the listbox dropdown with outside-click dismissal, full keyboard
   * navigation, focus trapping, and controlled visibility.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The menu/listbox element.
   */
  const menu = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["@click.outside"]() {
        this.closeSelectMenu();
      },

      ["@keydown"]($event) {
        this.keyboardNavigation($event);
      },

      ["@keydown.esc.prevent.stop"]() {
        this.closeSelectMenu();
      },

      ["@keydown.up.prevent.stop"]() {
        this.$focus.previous();
      },

      ["@keydown.down.prevent.stop"]() {
        this.$focus.next();
      },

      ["@keydown.home.prevent.stop"]() {
        this.$focus.first();
      },

      ["@keydown.end.prevent.stop"]() {
        this.$focus.last();
      },

      ["@keydown.page-up.prevent.stop"]() {
        this.$focus.first();
      },

      ["@keydown.page-down.prevent.stop"]() {
        this.$focus.last();
      },

      ["x-ref"]: "selectMenu",
      ["x-show"]: "opened",
      ["x-trap"]: "opened",
    }));
  };

  const root = (Alpine, el, { closeOnSelection = true }) => {
    Alpine.bind(el, () => ({
      /** Re-syncs options when the native select's option list changes. */
      ["@options-changed.stop"]() {
        this.syncOptions();
      },

      ["x-data"]() {
        return {
          keyboardTimeout: false,
          opened: false,
          options: [],

          /**
           * Syncs options from the native select on mount.
           */
          init() {
            this.syncOptions();
          },

          /**
           * Reads the current option elements from the native `<select>` and
           * updates the reactive `options` array.
           */
          syncOptions() {
            const select = this.$root.querySelector("[x-select\\:input]");

            const options = Array.from(select.options).map((option) => ({
              id: option.index,
              value: option.value,
              label: option.innerText,
              selected: option.selected,
              disabled: option.disabled,
              $el: option,
            }));

            this.options = options;
          },

          /**
           * Marks the given option as selected in both the reactive array and
           * the native `<select>`, then closes the menu if `closeOnSelection`
           * is enabled.
           * @param {object} option - The option object from the `options` array.
           */
          setSelected(option) {
            this.options.forEach((option) => {
              option.$el.removeAttribute("selected");
              option.$el.selected = false;
              option.selected = false;
            });

            option.$el.setAttribute("selected", "");
            option.$el.selected = true;
            option.selected = true;

            if (closeOnSelection) {
              this.closeSelectMenu();
            }
          },

          /**
           * Returns the currently selected option object, or undefined if none
           * is selected.
           * @returns {object|undefined}
           */
          getSelected() {
            return this.options.find((option) => option.selected === true);
          },

          /**
           * Opens the menu and focuses the selected option (or the first
           * focusable item when none is selected).
           */
          openSelectMenu() {
            this.opened = true;

            this.$nextTick(() => {
              const selected = this.$refs.selectMenu.querySelector(
                '[aria-selected="true"]',
              );

              if (selected) {
                this.$focus.focus(selected);
              } else {
                this.$focus.within(this.$refs.selectMenu).first();
              }
            });
          },

          /**
           * Closes the menu and returns focus to the toggle button.
           */
          closeSelectMenu() {
            this.opened = false;

            this.$nextTick(() => {
              this.$focus.focus(this.$refs.selectToggle);
            });
          },

          /**
           * Handles keyboard letter/number navigation inside the menu. Cycles
           * through options whose `label` attribute starts with the pressed key.
           * Uses a 50 ms debounce to allow for rapid key presses.
           * @param {KeyboardEvent} e - The keyboard event.
           */
          keyboardNavigation(e) {
            clearTimeout(this.keyboardTimeout);

            this.keyboardTimeout = setTimeout(() => {
              if (e.key.toUpperCase().match(/^[A-Z0-9]$/)) {
                let elements = this.$refs.selectMenu.querySelectorAll(
                  `[label^="${e.key.toUpperCase()}"]`,
                );
                let focusedEl, focusedIndex;

                // Find if there is already a focused item.
                elements.forEach((el, index) => {
                  if (document.activeElement === el) {
                    focusedEl = el;
                    focusedIndex = index;
                  }
                });

                // Focus the correct element.
                if (focusedEl) {
                  if (elements.length - 1 === focusedIndex) {
                    this.$focus.focus(elements[0]);
                  } else {
                    this.$focus.focus(elements[focusedIndex + 1]);
                  }
                } else {
                  this.$focus.focus(elements[0]);
                }
              }
            }, 50);
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("select", (el, { expression, value }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};

      if ("input" === value) {
        input(Alpine, el);
      } else if ("toggle" === value) {
        toggle(Alpine, el);
      } else if ("label" === value) {
        label(Alpine, el);
      } else if ("menu" === value) {
        menu(Alpine, el);
      } else {
        root(Alpine, el, options);
      }
    });
  });
})();
